import { useMemo, useState } from 'react';
import {
  AccordionDetails,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import { StyledButton } from 'components/Buttons';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import { CorrectiveInvoice, InvoiceLine } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { ImZoomIn } from 'react-icons/im';
import { MdDelete } from 'react-icons/md';
import {
  canEditInvoice,
  hasUnknownActivity,
  isForeignInvoice,
} from 'utils/invoiceFunctions.utils';
import {
  formatNetUnitPrice,
  numberWithCommas,
} from 'utils/numberFunctions.utils';
import * as DS from '../../../../details.styled';
import * as D from '../../InvoiceLinesPanel.css';
import { AddInvoiceLineDialog } from '../AddInvoiceLineDialog';
import { DeleteInvoiceDialog } from '../DeleteInvoiceLineDialog';
import { InvoiceLineDetailsDialog } from '../InvoiceLineDetailsDialog';
import { InvoiceLinesSummary } from '../InvoiceLinesSummary';
import { correctiveInvoiceLinesHeadCells } from './CorrectiveInvoiceLines.const';
import * as S from './CorrectiveInvoiceLines.styles';
import { CorrectiveInvoiceLinesTableData } from './CorrectiveInvoiceLines.types';

type Props = {
  invoice: CorrectiveInvoice;
  refetchInvoiceData: FetchInvoiceData;
};

function createTableData(
  index: number,
  id: number,
  code: string,
  quantity: number,
  unitCode: string,
  currencyAmount: number,
  currency: string,
  exchangeRate: number,
  netValue: number,
  taxRate: number,
  taxValue: number,
  grossValue: number,
  marked: boolean,
  correctiveIsFromOriginal: boolean,
): CorrectiveInvoiceLinesTableData {
  return {
    index,
    id,
    code,
    quantity,
    unitCode,
    currencyAmount: formatNetUnitPrice(currencyAmount),
    currency,
    exchangeRate: exchangeRate.toFixed(4),
    netValue: numberWithCommas(netValue),
    taxRate: taxRate + '%',
    taxValue: numberWithCommas(taxValue),
    grossValue: numberWithCommas(grossValue),
    marked,
    correctiveIsFromOriginal,
  };
}

export const CorrectiveInvoiceLines = ({
  invoice,
  refetchInvoiceData,
}: Props) => {
  const { user, allowedReplacementsByCode } = useAuth();

  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
  ]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDeleteInvoiceDialogOpen, setIsDeleteInvoiceDialogOpen] =
    useState(false);
  const [selectedLine, setSelectedLine] = useState<InvoiceLine | null>(null);
  const [isPreviousLineAction, setIsPreviousLineAction] = useState(false);

  const rowsPrevious = useMemo(
    () =>
      (invoice as CorrectiveInvoice)?.previousInvoiceLines.map((line) =>
        createTableData(
          line.ordinalNumber,
          line.id as number,
          line.activity.code,
          line.quantity,
          line.unit.code,
          line.currencyAmount.value,
          line.currencyAmount.currencyCode.currency,
          line.exchangeRate,
          line.amounts.netValue,
          line.amounts.taxRate.value,
          line.amounts.taxValue,
          line.amounts.grossValue,
          hasUnknownActivity(invoice, line.activity),
          !!line.correctiveIsFromOriginal,
        ),
      ),
    [invoice],
  );

  const rowsCorrected = useMemo(
    () =>
      (invoice as CorrectiveInvoice)?.correctedInvoiceLines.map((line) =>
        createTableData(
          line.ordinalNumber,
          line.id as number,
          line.activity.code,
          line.quantity,
          line.unit.code,
          line.currencyAmount.value,
          line.currencyAmount.currencyCode.currency,
          line.exchangeRate,
          line.amounts.netValue,
          line.amounts.taxRate.value,
          line.amounts.taxValue,
          line.amounts.grossValue,
          hasUnknownActivity(invoice, line.activity),
          !!line.correctiveIsFromOriginal,
        ),
      ),
    [invoice],
  );

  const canAddPreviousInvoiceLines = useMemo(
    () =>
      !invoice.deleted &&
      !invoice.generated &&
      !invoice.publicationDate &&
      canEditInvoice(invoice, user, allowedReplacementsByCode),
    [invoice, user, allowedReplacementsByCode],
  );

  const canAddCorrectedLines = useMemo(
    () =>
      !invoice.publicationDate &&
      !invoice.deleted &&
      canEditInvoice(invoice, user, allowedReplacementsByCode),
    [invoice, user, allowedReplacementsByCode],
  );

  const canDeletePreviousLines = useMemo(() => {
    if (invoice.invoiceClass.own && invoice.generated) {
      return false;
    }
    if (isForeignInvoice(invoice.invoiceClass) && !invoice.owner) {
      return false;
    }
    if (!invoice.publicationDate) {
      if (!(!invoice.invoiceClass.own && invoice.generated)) {
        return true;
      }
      return false;
    }
    return false;
  }, [invoice]);

  const handleOpenAddPreviousLineDialog = () => {
    setIsPreviousLineAction(true);
    setIsAddDialogOpen(true);
  };

  const handleOpenAddCorrectedLineDialog = () => {
    setIsPreviousLineAction(false);
    setIsAddDialogOpen(true);
  };

  const handleOpenPreviousLineDetails = (id: number) => {
    setSelectedLine(
      invoice?.previousInvoiceLines.find((line) => line.id === id) || null,
    );
    setIsPreviousLineAction(true);
    setIsDetailsDialogOpen(true);
  };

  const handleOpenCorrectedLineDetails = (id: number) => {
    setSelectedLine(
      invoice?.correctedInvoiceLines.find((line) => line.id === id) || null,
    );
    setIsPreviousLineAction(false);
    setIsDetailsDialogOpen(true);
  };

  const handleCloseLineDetails = () => {
    setIsDetailsDialogOpen(false);
    setSelectedLine(null);
  };

  const handleOpenDeletePreviousLineDialog = (id: number) => {
    setSelectedLine(
      invoice?.previousInvoiceLines.find((i) => i.id === id) || null,
    );
    setIsPreviousLineAction(true);
    setIsDeleteInvoiceDialogOpen(true);
  };

  const handleOpenDeleteCorrectedLineDialog = (id: number) => {
    setSelectedLine(
      invoice?.correctedInvoiceLines.find((i) => i.id === id) || null,
    );
    setIsPreviousLineAction(false);
    setIsDeleteInvoiceDialogOpen(true);
  };

  const handleCloseDeleteLineDialog = () => {
    setIsDeleteInvoiceDialogOpen(false);
    setSelectedLine(null);
  };

  return (
    <>
      <AddInvoiceLineDialog
        open={isAddDialogOpen}
        invoice={invoice}
        onClose={() => setIsAddDialogOpen(false)}
        refetchInvoiceData={refetchInvoiceData}
        isPrevious={isPreviousLineAction}
      />
      <InvoiceLineDetailsDialog
        open={isDetailsDialogOpen}
        invoice={invoice}
        invoiceLine={selectedLine}
        isPrevious={isPreviousLineAction}
        onClose={handleCloseLineDetails}
        refetchInvoiceData={refetchInvoiceData}
      />
      <DeleteInvoiceDialog
        open={isDeleteInvoiceDialogOpen}
        invoice={invoice}
        isCorrective={true}
        invoiceLine={selectedLine}
        isPrevious={isPreviousLineAction}
        refetchInvoiceData={refetchInvoiceData}
        onClose={handleCloseDeleteLineDialog}
      />
      <AccordionDetails sx={{ overflowX: 'auto' }}>
        <DS.Table>
          <TableHead>
            <TableRow>
              {correctiveInvoiceLinesHeadCells.map((cell) => (
                <StyledTableHeadCell
                  key={cell.id}
                  align={cell.align || 'left'}
                  padding='normal'
                  height='condensed'
                >
                  {cell.label}
                </StyledTableHeadCell>
              ))}
              <StyledTableHeadCell />
            </TableRow>
          </TableHead>

          <TableBody>
            <DS.TableRow>
              <StyledTableCell height='condensed' colSpan={12}>
                <S.InvoiceLinesSubtitle>
                  {t('was', { ns: 'invoice' })}
                </S.InvoiceLinesSubtitle>
              </StyledTableCell>
            </DS.TableRow>

            {rowsPrevious.map((row) => (
              <D.LinesTableRow
                key={row.id}
                hover
                $marked={row.marked}
                title={
                  row.marked ? t('unknownInvoiceMsg', { ns: 'invoice' }) : ''
                }
              >
                {correctiveInvoiceLinesHeadCells.map((cell) => (
                  <StyledTableCell
                    key={cell.id}
                    height='condensed'
                    align={cell.align || 'left'}
                    onClick={() => handleOpenPreviousLineDetails(row.id)}
                  >
                    {row[cell.id]}
                  </StyledTableCell>
                ))}
                {!invoice.deleted && (
                  <StyledTableCell height='condensed' width='8%'>
                    <D.StyledIconButton
                      size='small'
                      color='inherit'
                      title={t('button.details', { ns: 'misc' })}
                      onClick={() => handleOpenPreviousLineDetails(row.id)}
                    >
                      <ImZoomIn size={16} />
                    </D.StyledIconButton>
                    {canDeletePreviousLines && (
                      <D.StyledIconButton
                        size='small'
                        color='inherit'
                        title={t('delete', { ns: 'button' })}
                        onClick={() =>
                          handleOpenDeletePreviousLineDialog(row.id)
                        }
                      >
                        <MdDelete size={20} />
                      </D.StyledIconButton>
                    )}
                  </StyledTableCell>
                )}
              </D.LinesTableRow>
            ))}

            <DS.TableRow selected divider>
              <StyledTableCell height='condensed' colSpan={6} />
              <StyledTableCell height='condensed' align='right'>
                {t('total', { ns: 'invoice' })}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.previousTotals.netValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                -
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.previousTotals.taxValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.previousTotals.grossValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' />
            </DS.TableRow>

            <InvoiceLinesSummary taxTotals={invoice.previousTaxTotals} />

            {canAddPreviousInvoiceLines &&
              !(isForeignInvoice(invoice.invoiceClass) && !invoice.owner) && (
                <DS.TableRow>
                  <StyledTableCell
                    height='condensed'
                    colSpan={12}
                    align='right'
                  >
                    <StyledButton
                      $primary
                      disabled={invoice.deleted}
                      onClick={handleOpenAddPreviousLineDialog}
                    >
                      {t('button.addItem', { ns: 'misc' })}
                    </StyledButton>
                  </StyledTableCell>
                </DS.TableRow>
              )}

            <DS.TableRow>
              <StyledTableCell height='condensed' colSpan={12}>
                <S.InvoiceLinesSubtitle>
                  {t('shouldBe', { ns: 'invoice' })}
                </S.InvoiceLinesSubtitle>
              </StyledTableCell>
            </DS.TableRow>

            {rowsCorrected.map((row) => (
              <D.LinesTableRow
                key={row.id}
                hover
                $marked={row.marked}
                title={
                  row.marked ? t('unknownInvoiceMsg', { ns: 'invoice' }) : ''
                }
              >
                {correctiveInvoiceLinesHeadCells.map((cell) => (
                  <StyledTableCell
                    key={cell.id}
                    height='condensed'
                    align={cell.align || 'left'}
                    onClick={() => handleOpenCorrectedLineDetails(row.id)}
                  >
                    {row[cell.id]}
                  </StyledTableCell>
                ))}
                <StyledTableCell height='condensed' width='8%'>
                  {!invoice.deleted && (
                    <>
                      <D.StyledIconButton
                        size='small'
                        color='inherit'
                        title={t('button.details', { ns: 'misc' })}
                        onClick={() => handleOpenCorrectedLineDetails(row.id)}
                      >
                        <ImZoomIn size={16} />
                      </D.StyledIconButton>
                      {canEditInvoice(
                        invoice,
                        user,
                        allowedReplacementsByCode,
                      ) &&
                        !row.correctiveIsFromOriginal &&
                        !(
                          isForeignInvoice(invoice.invoiceClass) &&
                          !invoice.owner
                        ) && (
                          <D.StyledIconButton
                            size='small'
                            color='inherit'
                            title={t('button.delete', { ns: 'misc' })}
                            onClick={() =>
                              handleOpenDeleteCorrectedLineDialog(row.id)
                            }
                          >
                            <MdDelete size={20} />
                          </D.StyledIconButton>
                        )}
                    </>
                  )}
                </StyledTableCell>
              </D.LinesTableRow>
            ))}

            <DS.TableRow selected divider>
              <StyledTableCell height='condensed' colSpan={6} />
              <StyledTableCell height='condensed' align='right'>
                {t('total', { ns: 'invoice' })}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.correctedTotals.netValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                -
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.correctedTotals.taxValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.correctedTotals.grossValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' />
            </DS.TableRow>

            <InvoiceLinesSummary taxTotals={invoice.correctedTaxTotals} />

            {canAddCorrectedLines &&
              !(isForeignInvoice(invoice.invoiceClass) && !invoice.owner) && (
                <DS.TableRow>
                  <StyledTableCell
                    height='condensed'
                    colSpan={12}
                    align='right'
                  >
                    <StyledButton
                      $primary
                      disabled={invoice.deleted}
                      onClick={handleOpenAddCorrectedLineDialog}
                    >
                      {t('button.addItem', { ns: 'misc' })}
                    </StyledButton>
                  </StyledTableCell>
                </DS.TableRow>
              )}

            <DS.TableRow selected divider>
              <StyledTableCell height='condensed' colSpan={6} />
              <StyledTableCell height='condensed' align='right'>
                {t('totalDifference', { ns: 'invoice' })}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.netValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                -
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.taxValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.grossValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' />
            </DS.TableRow>

            <InvoiceLinesSummary taxTotals={invoice.taxTotals} />
          </TableBody>
        </DS.Table>
      </AccordionDetails>
    </>
  );
};
